import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';

const POLL_INTERVAL = 30000;
const RETRY_DELAY = 5000;

let _currentVersion = null;
let _updateAvailable = false;
let _updating = false;

export function isScanActive() {
  return document.querySelector('[data-scan-active="true"]') !== null;
}

export function useUpdateChecker(enabled = true) {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updating, setUpdating] = useState(false);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  const doReload = useCallback(() => {
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage('SKIP_WAITING');
    }
    window.location.reload();
  }, []);

  const checkForUpdate = useCallback(async () => {
    if (!enabled || _updating) return;

    try {
      const res = await fetch('/api/version', { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      const serverVersion = data.version;

      if (_currentVersion === null) {
        _currentVersion = serverVersion;
        return;
      }

      if (serverVersion && serverVersion !== _currentVersion && !_updateAvailable) {
        _updateAvailable = true;
        if (mountedRef.current) {
          setUpdateAvailable(true);
          toast.info(
            'A new version is available. Updating automatically...',
            { autoClose: false, toastId: 'app-update', position: 'top-center' }
          );
        }

        const tryReload = () => {
          if (isScanActive() || document.querySelector('form[data-submitting="true"]')) {
            setTimeout(tryReload, RETRY_DELAY);
            return;
        }
          doReload();
        };

        setTimeout(tryReload, 3000);
      }
    } catch {
      // Network error — silently ignore, will retry on next interval
    }
  }, [enabled, doReload]);

  useEffect(() => {
    mountedRef.current = true;
    checkForUpdate();
    timerRef.current = setInterval(checkForUpdate, POLL_INTERVAL);

    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [checkForUpdate]);

  return { updateAvailable, updating, checkForUpdate };
}
